"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BALL_IN_COURT_LABELS = exports.ACTOR_TYPE_LABELS = exports.eventLogStoryQuerySchema = exports.eventLogNewerQuerySchema = exports.eventLogQuerySchema = exports.eventLogFilterSchema = exports.EVENT_LOG_SEARCH_MIN = exports.EVENT_LOG_NEWER_CAP = exports.EVENT_LOG_STORY_MAX = exports.EVENT_LOG_EXPORT_MAX_ROWS = exports.EVENT_LOG_COUNT_CAP = exports.EVENT_LOG_PAGE_SIZE = void 0;
exports.turnsOf = turnsOf;
const zod_1 = require("zod");
const enums_1 = require("./enums");
const schemas_1 = require("./schemas");
// S7 — the event log, and the inspector over it (Admin · Event log).
//
// `events` is the spine of goal 1: every metric in the product is computed from it
// and nothing is stored (non-negotiable #10). The inspector is how anybody checks
// that claim, which is why it filters on the same four things a report groups by —
// entity, type, actor and date range.
//
// Admin redesign 2026-11-02, Event log A: a plain sentence per event, the record it is about (name · job · venture),
// a venture filter, "Today" by default, a before/after table, an export, and one record's story.
/** One page of the table: 50, as before the redesign. */
exports.EVENT_LOG_PAGE_SIZE = 50;
/**
 * The count stops here, and so do the pages: "10,000+ events" rather than counting a whole table on every page.
 * Past it the filters need narrowing, the same limit as the export.
 */
exports.EVENT_LOG_COUNT_CAP = 10_000;
/** The most rows one export holds; a wider one is refused with "narrow the filters" (as the access log's). */
exports.EVENT_LOG_EXPORT_MAX_ROWS = 10_000;
/** One record's story shows at most this many of its events, the newest ones. */
exports.EVENT_LOG_STORY_MAX = 500;
/** "N new · Show" counts up to here, then reads "99+". */
exports.EVENT_LOG_NEWER_CAP = 99;
/** 06-qol §6: a search needs two characters; one character is no search at all. */
exports.EVENT_LOG_SEARCH_MIN = 2;
/** Text that is no search at all (blank, or one character) is dropped rather than refused. */
const searchSchema = zod_1.z
    .string()
    .trim()
    .max(120)
    .transform((value) => (value.length < exports.EVENT_LOG_SEARCH_MIN ? undefined : value))
    .optional();
/** Every filter, without paging: what the table, the count, the export and "N new" share. */
exports.eventLogFilterSchema = zod_1.z.object({
    entityType: zod_1.z.string().trim().max(60).optional(),
    entityId: schemas_1.uuidSchema.optional(),
    eventType: zod_1.z.string().trim().max(80).optional(),
    actorId: schemas_1.uuidSchema.optional(),
    /**
     * The filter S7 exists for. "Auto-advance, SLA breach and retention sweeps must
     * be distinguishable from human action in every report" — a report that cannot
     * tell them apart is misleading, so the inspector can separate them in one click.
     */
    actorType: zod_1.z.enum(['user', 'system']).optional(),
    ventureId: schemas_1.uuidSchema.optional(),
    ballInCourt: zod_1.z.enum(enums_1.BALL_IN_COURT).optional(),
    from: zod_1.z.iso.datetime().optional(),
    to: zod_1.z.iso.datetime().optional(),
    q: searchSchema,
});
exports.eventLogQuerySchema = exports.eventLogFilterSchema.extend({
    page: zod_1.z.coerce.number().int().positive().default(1),
    pageSize: zod_1.z.coerce.number().int().positive().max(200).default(exports.EVENT_LOG_PAGE_SIZE),
});
/** "N new · Show": how many rows the same filters match that are newer than the newest one on screen. */
exports.eventLogNewerQuerySchema = exports.eventLogFilterSchema.extend({ afterId: schemas_1.uuidSchema });
exports.eventLogStoryQuerySchema = zod_1.z.object({
    entityType: zod_1.z.string().trim().min(1).max(60),
    entityId: schemas_1.uuidSchema,
});
exports.ACTOR_TYPE_LABELS = {
    user: 'A person',
    system: 'The system',
};
exports.BALL_IN_COURT_LABELS = {
    recruiter: 'Recruiter',
    hiring_manager: 'Hiring manager',
    candidate: 'Candidate',
    approver: 'Approver',
    panelist: 'Panelist',
    none: 'Nobody',
};
const MINUTE_MS = 60_000;
/**
 * Whose turn it was, worked out from the ball-in-court each event carries (S7: the same field the SLA reports
 * split time by, so the strip agrees with the numbers). Events that carry none leave the turn where it was.
 * A record whose last turn is "Nobody" (rejected, closed) stops there rather than stretching to today.
 */
function turnsOf(events, now = new Date()) {
    const changes = [];
    const ordered = [...events].sort((left, right) => Date.parse(left.occurredAt) - Date.parse(right.occurredAt));
    for (const event of ordered) {
        if (event.ballInCourt === null)
            continue;
        if (changes[changes.length - 1]?.holder === event.ballInCourt)
            continue;
        changes.push({ holder: event.ballInCourt, at: event.occurredAt });
    }
    const spans = [];
    changes.forEach((change, index) => {
        const next = changes[index + 1];
        if (!next && change.holder === 'none')
            return;
        const end = next ? Date.parse(next.at) : Math.max(now.getTime(), Date.parse(change.at));
        spans.push({
            holder: change.holder,
            from: change.at,
            to: next ? next.at : null,
            minutes: Math.max(0, Math.round((end - Date.parse(change.at)) / MINUTE_MS)),
        });
    });
    return spans;
}
//# sourceMappingURL=event-log.js.map